// A dialog inside the shadow root: overlay, focus trap, Esc and overlay-click close, body scroll
// lock, focus returned to what opened it. Shared by the button and tab layouts, and (via the
// `variant`/`lockScroll`/`modal`/`onClose` options) the card/corner popup styles. A caller that passes none
// of those keeps the original behavior byte-for-byte — only `variant` turns on entrance/exit
// animation and the exit-close delay. `modal: false` (the desktop corner popup) leaves the page usable:
// no aria-modal, no focus taken on open, no Tab trap. `topLayer: true` (the form launcher) draws the
// overlay as a native <dialog> opened with showModal(): the browser's top layer ignores ancestor
// transforms, so a launcher tag inside a site's scroll-reveal block still covers the whole page. A browser with
// no <dialog> (Safari before 15.4) gets the fixed <div> overlay instead, so the form still opens.
import { el, touchScreen } from './dom'

// A tabindex="-1" link or button (a popup photo that repeats its button's link) is out of the tab order, so it
// takes neither the first focus nor a place in the Tab loop.
const FOCUSABLE = 'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
// Matches each variant's CSS exit transition-duration (popup_styles.js, launcher_styles.js); kept in one
// place so JS never hides the dialog mid-fade.
const EXIT_MS = { card: 420, corner: 420, dialog: 180, sheet: 420 }

function mq(query, fallback) {
  try { return window.matchMedia ? window.matchMedia(query).matches : fallback } catch (error) { return fallback }
}

// One page-wide scroll lock: a form and a popup open at once each saved the other's `overflow: hidden` as the value
// to restore, and the page stayed frozen after both closed. Only the first lock saves, only the last release restores.
let locks = 0
let pageOverflow = ''
function lockPage() {
  if (locks++ === 0) { pageOverflow = document.body.style.overflow; document.body.style.overflow = 'hidden' }
}
function unlockPage() {
  if (locks > 0 && --locks === 0) document.body.style.overflow = pageOverflow
}

// Popups wait their turn: nothing opens over a form the guest is filling in, nor the moment they close it.
const IDLE_GAP_MS = 1200
let openModals = 0
let idleWaiters = []
export function whenModalsIdle(fn) {
  if (openModals === 0) return fn()
  idleWaiters.push(fn)
}
let idleTimer = null
function modalClosed() {
  openModals = Math.max(0, openModals - 1)
  if (openModals > 0 || !idleWaiters.length) return
  // The gap counts from the latest close: a modal opened and closed inside it would otherwise let the first timer fire.
  clearTimeout(idleTimer)
  idleTimer = setTimeout(() => {
    if (openModals > 0) return
    const waiting = idleWaiters
    idleWaiters = []
    waiting.forEach((fn) => whenModalsIdle(fn))
  }, IDLE_GAP_MS)
}

// A phone keyboard covers the bottom of a fixed overlay without resizing it (iOS, and Chrome on Android since 108),
// so the overlay follows the visual viewport: the sheet ends at the keyboard and scrolls inside itself, and the
// browser has no reason to pan the page behind it. `data-keyboard` lets the CSS unpin sticky buttons meanwhile.
export function fitToViewport(overlay, dialog) {
  const viewport = window.visualViewport
  if (!viewport) return () => {}
  let wasKeyboard = false
  const sync = () => {
    const covered = window.innerHeight - viewport.height
    const keyboard = covered > 120
    overlay.toggleAttribute('data-keyboard', keyboard)
    overlay.style.top = keyboard ? `${Math.max(0, viewport.offsetTop)}px` : ''
    overlay.style.height = keyboard ? `${viewport.height}px` : ''
    overlay.style.bottom = keyboard ? 'auto' : ''
    // Only the moment the keyboard arrives needs the field pulled back into view; the browser handles later focus moves.
    const arrived = keyboard && !wasKeyboard
    wasKeyboard = keyboard
    if (!arrived) return
    const active = dialog.getRootNode().activeElement
    if (active && dialog.contains(active) && active !== dialog) active.scrollIntoView({ block: 'nearest' })
  }
  viewport.addEventListener('resize', sync)
  viewport.addEventListener('scroll', sync)
  sync()
  return () => {
    viewport.removeEventListener('resize', sync)
    viewport.removeEventListener('scroll', sync)
    overlay.removeAttribute('data-keyboard')
    overlay.style.top = overlay.style.height = overlay.style.bottom = ''
  }
}

export function createModal({ container, label, labelledBy, variant, lockScroll = true, modal = true, topLayer: wantsTopLayer = false, onClose }) {
  const topLayer = wantsTopLayer && typeof window.HTMLDialogElement === 'function'
  const body = el('div', { class: 'ef-modal-body' })
  const close = el('button', {
    type: 'button', class: 'ef-modal-close', 'aria-label': 'Close',
    html: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>'
  })
  const dialogAttrs = {
    class: `ef-modal-dialog${variant ? ` ef-modal-dialog--${variant}` : ''}`,
    role: 'dialog', tabindex: '-1'
  }
  if (modal) dialogAttrs['aria-modal'] = 'true'
  if (labelledBy) dialogAttrs['aria-labelledby'] = labelledBy
  else dialogAttrs['aria-label'] = label
  const dialog = el('div', dialogAttrs, [close, body])
  const overlay = el(topLayer ? 'dialog' : 'div', { class: `ef-modal${variant ? ` ef-modal--${variant}` : ''}`, hidden: true }, dialog)
  container.append(overlay)
  if (topLayer) {
    // Esc is handled by onKey so the exit animation runs; a close the browser forces anyway still tidies up.
    overlay.addEventListener('cancel', (event) => event.preventDefault())
    overlay.addEventListener('close', () => {
      if (!api.open) return
      overlay.removeEventListener('keydown', onKey)
      overlay.classList.remove('ef-modal--open')
      returnFocus = modal
      finish()
    })
  }

  let opener = null
  let returnFocus = false
  let unfit = null

  function finish() {
    if (overlay.hidden) return
    overlay.hidden = true
    if (topLayer && overlay.open) overlay.close()
    if (lockScroll) unlockPage()
    if (unfit) { unfit(); unfit = null }
    if (modal) modalClosed()
    if (returnFocus && opener && opener.isConnected) opener.focus({ preventScroll: true })
    if (onClose) onClose()
  }

  const focusables = () => [...dialog.querySelectorAll(FOCUSABLE)].filter((node) => !node.hidden && node.offsetParent !== null)

  function onKey(event) {
    if (event.key === 'Escape') return api.close()
    if (!modal || event.key !== 'Tab') return
    const nodes = focusables()
    if (!nodes.length) return
    const first = nodes[0]
    const last = nodes[nodes.length - 1]
    const active = dialog.getRootNode().activeElement
    if (event.shiftKey && (active === first || active === dialog)) { event.preventDefault(); last.focus() }
    else if (!event.shiftKey && active === last) { event.preventDefault(); first.focus() }
  }

  const api = {
    body,
    dialog,
    get open() { return !overlay.hidden },
    show(from) {
      if (api.open) return
      opener = from || null
      overlay.hidden = false
      if (topLayer && !overlay.open) {
        // showModal() focuses the first field itself, with scrolling: on a phone the sheet is still below the screen,
        // so Safari scrolled the page and the overlay after it and the sheet landed off the top. Inert content leaves
        // it only the full-screen overlay to focus; the scroll restore covers a browser without `inert`.
        const [x, y] = [window.scrollX, window.scrollY]
        dialog.inert = true
        overlay.showModal()
        dialog.inert = false
        overlay.scrollTop = 0
        if (window.scrollX !== x || window.scrollY !== y) window.scrollTo({ left: x, top: y, behavior: 'instant' })
      }
      if (lockScroll) {
        lockPage()
        unfit = fitToViewport(overlay, dialog)
      }
      if (modal) openModals += 1
      overlay.addEventListener('keydown', onKey)
      if (variant) {
        // Un-hiding and adding the class in the same tick starts the transition already at its
        // end state; two rAFs guarantee a painted frame at the "closed" state first.
        requestAnimationFrame(() => requestAnimationFrame(() => overlay.classList.add('ef-modal--open')))
      }
      if (!modal) return
      // On a touch screen a focused field would raise the keyboard over half the form before the guest has read it
      // (and iOS pans the page for a field that is still sliding in). There the dialog itself takes focus.
      const first = (!touchScreen() && focusables().find((node) => node !== close)) || dialog
      first.focus({ preventScroll: true })
    },
    close() {
      if (!api.open) return
      overlay.removeEventListener('keydown', onKey)
      // A non-modal dialog never took focus, so it only hands focus back if the visitor moved into it.
      returnFocus = modal || dialog.contains(dialog.getRootNode().activeElement)
      if (!variant) return finish()
      overlay.classList.remove('ef-modal--open')
      // Below 576px the launcher dialog is a bottom sheet with the popup sheet's slower slide.
      const exit = variant === 'dialog' && mq('(max-width: 575px)', false) ? 'sheet' : variant
      const delay = mq('(prefers-reduced-motion: reduce)', false) ? 120 : (EXIT_MS[exit] || 300)
      setTimeout(finish, delay)
    },
    // Tears down without the exit animation or onClose, for an owner that is removing the dialog itself.
    dispose() { onClose = null; finish() }
  }
  // A phone sheet starts below the screen, which gives its `overflow: hidden` scrim something to scroll to: focus moving
  // into the sheet scrolled the scrim and left the sheet off the top. Only a scrim that scrolls by design keeps its offset.
  let scrollsByDesign = null // read once the overlay is in the document; a style recalc per scroll event is too much
  overlay.addEventListener('scroll', () => {
    if (scrollsByDesign === null) scrollsByDesign = /auto|scroll/.test(getComputedStyle(overlay).overflowY)
    if (overlay.scrollTop && !scrollsByDesign) overlay.scrollTop = 0
  })
  close.addEventListener('click', () => api.close())
  overlay.addEventListener('pointerdown', (event) => { if (event.target === overlay) api.close() })
  return api
}
